import {
  LoyaltyMovementSchema,
  LoyaltyOutstandingSchema,
  LoyaltySettingsChangeSchema,
  LoyaltySettingsVersionSchema,
  LoyaltySummarySchema,
  OrderPointsSchema,
  type LoyaltyAdjustmentRequest,
  type UpdateLoyaltySettingsRequest,
} from '@deli/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as z from 'zod';
import { getJson, sendJson } from './client.js';

/** Q-points (I13): settings with their history, a customer's points, reports, an order's points. */
export const useLoyaltySettings = () =>
  useQuery({
    queryKey: ['loyalty', 'settings'],
    queryFn: ({ signal }) =>
      getJson('/api/v1/loyalty/settings', z.array(LoyaltySettingsVersionSchema), { signal }),
  });

export function useUpdateLoyaltySettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateLoyaltySettingsRequest) =>
      sendJson('POST', '/api/v1/loyalty/settings', body, LoyaltySettingsChangeSchema),
    onSuccess: (change) => {
      if (change.applied) void client.invalidateQueries({ queryKey: ['loyalty'] });
    },
  });
}

export const useCustomerLoyalty = (customerId: string) =>
  useQuery({
    queryKey: ['loyalty', 'customer', customerId],
    queryFn: ({ signal }) =>
      getJson(`/api/v1/customers/${customerId}/loyalty`, LoyaltySummarySchema, { signal }),
  });

export function useAdjustPoints(customerId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: LoyaltyAdjustmentRequest) =>
      sendJson(
        'POST',
        `/api/v1/customers/${customerId}/loyalty/adjustments`,
        body,
        LoyaltySummarySchema,
      ),
    onSuccess: (summary) => {
      client.setQueryData(['loyalty', 'customer', customerId], summary);
    },
  });
}

export const useLoyaltyOutstanding = (asOf: string | null) =>
  useQuery({
    queryKey: ['loyalty', 'outstanding', asOf],
    queryFn: ({ signal }) =>
      getJson(
        `/api/v1/loyalty/reports/outstanding${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''}`,
        LoyaltyOutstandingSchema,
        { signal },
      ),
  });

export const useLoyaltyMovement = (from: string, to: string) =>
  useQuery({
    queryKey: ['loyalty', 'movement', from, to],
    queryFn: ({ signal }) =>
      getJson(
        `/api/v1/loyalty/reports/movement?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        LoyaltyMovementSchema,
        { signal },
      ),
  });

export const useOrderPoints = (orderId: string, updatedAt: string) =>
  useQuery({
    // Keyed by the order's updatedAt: any change to the order refreshes its points.
    queryKey: ['orders', 'points', orderId, updatedAt],
    queryFn: ({ signal }) =>
      getJson(`/api/v1/orders/${orderId}/points`, OrderPointsSchema, { signal }),
  });
